// ============================================================================
// AFB-0075 / PR 3 — onboarding packets, inside the e-sign surface on
// /money/envelopes, next to the template manager PR 2 put there.
//
// PR 1 gave a template its document. PR 2 gave it a type, a checklist item,
// signing roles and merge bindings. None of that answers the question Antonela
// actually asks eight times a hire: WHICH of these documents does this person
// get? "W2 onboarding" is eight templates, three of which are state withholding
// forms that no one person ever receives together. A packet is where that lives.
//
// It lives here rather than behind its own route because a packet is made of
// templates and is edited in the same sitting as they are — a second URL would
// mean loading the same template list twice and switching between two screens
// to answer one question.
//
// The rules are DATA, on esign_packet_documents, never in code: a new state
// form is a row, not a deploy. And nothing on this screen decides anything —
// selection and page arithmetic are in ./packet-composition, which is pure and
// tested, because that arithmetic decides which page a signature box lands on.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Layers, Loader2, Plus, Save } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import { errorMessage } from '../../shared/errors';
import { EsignTemplate } from './envelope-lib';
import {
  PacketDocumentPatch, PacketDocumentRow, PacketMeta, PacketRow, addPacketDocument,
  createPacket, listPacketDocuments, listPackets, moveDocument, removePacketDocument,
  reorderPacketDocuments, savePacketDocument, savePacketMeta,
} from './packet-lib';
import { PacketDocuments } from './packet-documents';
import { PacketPreview } from './packet-preview';

const INPUT =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-xs text-tcs-text';
const LABEL = 'block text-[11px] font-medium text-tcs-text-secondary';
const PRIMARY =
  'inline-flex min-h-[36px] items-center gap-1.5 rounded-md bg-tcs-accent-ink px-3 text-xs font-semibold text-tcs-accent-on disabled:opacity-50';
const SECONDARY =
  'inline-flex min-h-[36px] items-center gap-1.5 rounded-md border border-tcs-border px-2 text-[11px] text-tcs-text-secondary disabled:opacity-50';

interface PacketMetaEditorProps {
  packet: PacketRow;
  busy: boolean;
  onSave: (meta: Partial<PacketMeta>) => void;
}

/**
 * Name, description, the covering copy, and whether the packet is offered.
 *
 * The three email fields are the packet's, not the template's: a hire receives
 * ONE envelope carrying the whole packet, so one subject line and one note to
 * the signer cover all of it. A per-template subject would have nowhere to go.
 */
function PacketMetaEditor({ packet, busy, onSave }: PacketMetaEditorProps) {
  const [name, setName] = useState(packet.name);
  const [description, setDescription] = useState(packet.description ?? '');
  const [subject, setSubject] = useState(packet.email_subject ?? '');
  const [body, setBody] = useState(packet.email_body ?? '');
  const [note, setNote] = useState(packet.note_to_signer ?? '');
  const [isActive, setIsActive] = useState(packet.is_active);

  // The parent replaces the row with the re-read one after every save, so the
  // drafts follow what is stored rather than drifting away from it.
  useEffect(() => {
    setName(packet.name);
    setDescription(packet.description ?? '');
    setSubject(packet.email_subject ?? '');
    setBody(packet.email_body ?? '');
    setNote(packet.note_to_signer ?? '');
    setIsActive(packet.is_active);
  }, [packet]);

  return (
    <div className="space-y-1.5">
      <div className="grid gap-1.5 sm:grid-cols-2">
        <div>
          <label className={LABEL} htmlFor={`packet-name-${packet.packet_id}`}>Name</label>
          <input
            id={`packet-name-${packet.packet_id}`}
            value={name}
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
            className={`mt-1 ${INPUT}`}
          />
        </div>
        <div>
          <label className={LABEL} htmlFor={`packet-desc-${packet.packet_id}`}>
            Description
          </label>
          <input
            id={`packet-desc-${packet.packet_id}`}
            value={description}
            disabled={busy}
            placeholder="Who this packet is for"
            onChange={(e) => setDescription(e.target.value)}
            className={`mt-1 ${INPUT}`}
          />
        </div>
      </div>
      <div>
        <label className={LABEL} htmlFor={`packet-subject-${packet.packet_id}`}>
          Email subject
        </label>
        <input
          id={`packet-subject-${packet.packet_id}`}
          value={subject}
          disabled={busy}
          placeholder="Welcome to Terminal Cleaning — a few documents to sign"
          onChange={(e) => setSubject(e.target.value)}
          className={`mt-1 ${INPUT}`}
        />
      </div>
      <div>
        <label className={LABEL} htmlFor={`packet-body-${packet.packet_id}`}>Email body</label>
        <textarea
          id={`packet-body-${packet.packet_id}`}
          value={body}
          rows={3}
          disabled={busy}
          onChange={(e) => setBody(e.target.value)}
          className={`mt-1 ${INPUT}`}
        />
      </div>
      <div>
        <label className={LABEL} htmlFor={`packet-note-${packet.packet_id}`}>
          Note to the signer
        </label>
        <textarea
          id={`packet-note-${packet.packet_id}`}
          value={note}
          rows={2}
          disabled={busy}
          onChange={(e) => setNote(e.target.value)}
          className={`mt-1 ${INPUT}`}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-1 text-[11px] text-tcs-text-secondary">
          <input
            type="checkbox"
            disabled={busy}
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
          Active — offered when starting an onboarding
        </label>
        <button
          type="button"
          disabled={busy || name.trim().length < 3}
          onClick={() => onSave({
            name, description, email_subject: subject, email_body: body,
            note_to_signer: note, is_active: isActive,
          })}
          className={PRIMARY}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                : <Save className="h-3.5 w-3.5" aria-hidden />}
          Save packet
        </button>
      </div>
    </div>
  );
}

export interface PacketManagerProps {
  /** Active templates, already loaded by the page. */
  templates: EsignTemplate[];
}

export function PacketManager({ templates }: PacketManagerProps) {
  const { employee } = useAuth();
  const [packets, setPackets] = useState<PacketRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>('');
  const [documents, setDocuments] = useState<PacketDocumentRow[]>([]);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadPackets = useCallback(async () => {
    try {
      setPackets(await listPackets());
      setLoadError(null);
    } catch (e) {
      setLoadError(errorMessage(e, 'Could not load the packets.'));
    }
  }, []);

  const loadDocuments = useCallback(async (packet_id: string) => {
    if (packet_id === '') {
      setDocuments([]);
      return;
    }
    try {
      setDocuments(await listPacketDocuments(packet_id));
      setLoadError(null);
    } catch (e) {
      setLoadError(errorMessage(e, 'Could not load the documents on this packet.'));
    }
  }, []);

  useEffect(() => {
    void loadPackets();
  }, [loadPackets]);

  useEffect(() => {
    void loadDocuments(selected);
  }, [loadDocuments, selected]);

  const packet = (packets ?? []).find((p) => p.packet_id === selected) ?? null;

  /**
   * Run one write and re-read what it touched.
   *
   * Every action here has the same shape — busy, write, re-read, say what
   * happened — and the re-read is not optional: packet_id and link_id are
   * assigned by triggers, so the row that comes back is the only place the
   * assigned id can be seen.
   */
  async function run(
    key: string,
    write: () => Promise<void>,
    done: string,
    failed: string,
  ) {
    setBusy(key);
    setError(null);
    setMsg(null);
    try {
      await write();
      await loadPackets();
      await loadDocuments(selected);
      setMsg(done);
    } catch (e) {
      setError(errorMessage(e, failed));
    } finally {
      setBusy(null);
    }
  }

  async function create() {
    setBusy('new');
    setError(null);
    setMsg(null);
    try {
      // packet_id is NEVER sent — a BEFORE INSERT trigger assigns PKT-NNNN.
      const created = await createPacket({
        name: newName.trim(),
        created_by_eid: employee?.employee_id ?? null,
      });
      setNewName('');
      await loadPackets();
      setSelected(created.packet_id);
      setMsg(`Created ${created.packet_id} — add its documents below.`);
    } catch (e) {
      setError(errorMessage(e, 'Could not create the packet.'));
    } finally {
      setBusy(null);
    }
  }

  function saveMeta(meta: Partial<PacketMeta>) {
    if (!packet) return;
    void run(
      packet.packet_id,
      () => savePacketMeta(packet.packet_id, meta),
      `Saved "${meta.name ?? packet.name}".`,
      'Could not save the packet.',
    );
  }

  function addDocument(template_id: string) {
    if (!packet) return;
    const name = templates.find((t) => t.template_id === template_id)?.name ?? template_id;
    return run(
      'add',
      () => addPacketDocument({
        packet_id: packet.packet_id,
        template_id,
        // Last in the list. link_id is assigned by a trigger and never sent.
        sort_order: documents.length + 1,
      }),
      `Added "${name}" to the end of the packet. Set who gets it below.`,
      'Could not add that document.',
    );
  }

  function saveDocument(link_id: string, patch: PacketDocumentPatch) {
    return run(
      link_id,
      () => savePacketDocument(link_id, patch),
      'Saved. Check the preview below to see who that changes it for.',
      'Could not save the rules on that document.',
    );
  }

  function removeDocument(row: PacketDocumentRow) {
    const name = templates.find((t) => t.template_id === row.template_id)?.name
      ?? row.template_id;
    if (
      !window.confirm(
        `Take "${name}" off this packet? The template itself is untouched, and ` +
          `envelopes already sent are unaffected.`,
      )
    ) {
      return undefined;
    }
    return run(
      row.link_id,
      () => removePacketDocument(row.link_id),
      `Removed "${name}" from the packet.`,
      'Could not remove that document.',
    );
  }

  function move(link_id: string, delta: -1 | 1) {
    // Reordered purely first, then written back as 1..n — see moveDocument for
    // why the whole list is renumbered rather than two rows swapped.
    const reordered = moveDocument(documents, link_id, delta);
    setDocuments(reordered);
    return run(
      link_id,
      () => reorderPacketDocuments(reordered),
      'Reordered. Every later document now starts on a different page.',
      'Could not save the new order.',
    );
  }

  return (
    <div className="mt-4 border-t border-tcs-border pt-3">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-tcs-text">
        <Layers className="h-3.5 w-3.5" aria-hidden /> Onboarding packets
      </p>
      <p className="text-[11px] text-tcs-text-muted">
        An ordered set of templates plus the rules for who gets which of them.
        One envelope, one signing session, the right state form.
      </p>

      {loadError && (
        <p role="alert" className="mt-2 text-[11px] text-st-danger-fg">{loadError}</p>
      )}

      {packets === null && !loadError && (
        <Loader2 className="mt-2 h-4 w-4 animate-spin text-tcs-text-muted" aria-label="Loading packets" />
      )}

      {packets !== null && (
        <>
          <ul className="mt-2 flex flex-wrap gap-1.5">
            {packets.map((p) => (
              <li key={p.packet_id}>
                <button
                  type="button"
                  aria-pressed={selected === p.packet_id}
                  onClick={() => setSelected(selected === p.packet_id ? '' : p.packet_id)}
                  className={`rounded-full border px-2.5 py-1 text-[11px] ${
                    selected === p.packet_id
                      ? 'border-tcs-accent-ink bg-tcs-accent-ink text-tcs-accent-on'
                      : 'border-tcs-border bg-tcs-raised text-tcs-text-secondary'
                  }`}
                >
                  {p.name}
                  {!p.is_active && ' · inactive'}
                </button>
              </li>
            ))}
            {packets.length === 0 && (
              <li className="text-[11px] italic text-tcs-text-muted">
                No packets yet.
              </li>
            )}
          </ul>

          <div className="mt-2 flex flex-wrap items-end gap-2">
            <div className="min-w-[220px] flex-1">
              <label className={LABEL} htmlFor="packet-new-name">New packet</label>
              <input
                id="packet-new-name"
                value={newName}
                disabled={busy !== null}
                placeholder="e.g. Partner onboarding"
                onChange={(e) => setNewName(e.target.value)}
                className={`mt-1 ${INPUT}`}
              />
            </div>
            <button
              type="button"
              disabled={busy !== null || newName.trim().length < 3}
              onClick={() => void create()}
              className={SECONDARY}
            >
              {busy === 'new' ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                              : <Plus className="h-3 w-3" aria-hidden />}
              Create packet
            </button>
          </div>

          {packet && (
            <div className="mt-3 space-y-3 rounded-md border border-tcs-border bg-tcs-bg-muted p-2">
              <p className="text-[11px] text-tcs-text-muted">
                {packet.packet_id} · created{' '}
                {new Date(packet.created_at).toLocaleDateString()}
              </p>
              <PacketMetaEditor
                packet={packet}
                busy={busy === packet.packet_id}
                onSave={saveMeta}
              />
              <PacketDocuments
                documents={documents}
                templates={templates}
                busy={busy}
                onAdd={async (id) => { await addDocument(id); }}
                onSave={async (id, patch) => { await saveDocument(id, patch); }}
                onRemove={async (row) => { await removeDocument(row); }}
                onMove={async (id, delta) => { await move(id, delta); }}
              />
              <PacketPreview
                packetName={packet.name}
                documents={documents}
                templates={templates}
              />
            </div>
          )}
        </>
      )}

      {msg && <p className="mt-2 text-[11px] text-st-ok-fg">{msg}</p>}
      {error && <p role="alert" className="mt-2 text-[11px] text-st-danger-fg">{error}</p>}
    </div>
  );
}
